
import React, { useState, useEffect } from 'react';
import { Camera, X, Maximize2, ChevronDown, Sparkles, AlertCircle, Loader2, Key, Download, Plus, User, UserPlus, Users, Info, Settings2, Image as ImageIcon, Crosshair, Zap, ChevronRight, RotateCcw, Lock, ExternalLink } from 'lucide-react';
import { CreativeBrief, CampaignImage, AppStep, SubjectProfile } from './types';
import { generateCampaignConcepts, generateTwinImage } from './services/geminiService';

// Removed global declaration for aistudio to prevent runtime confusion

const REALISM_PROFILES = [
  "Phase One XF (Medium Format)", "Leica M11 (Documentary)", "Arri Alexa Cinematic", "Fujifilm 400H (Analog)", "Shot on iPhone (Unfiltered)", "National Geographic Editorial", "Raw Studio (No Post-Processing)", "Magnum Photojournalism"
];

const LIGHTING_OPTIONS = [
  "Raw Window Light", "Hard Direct Sun", "Natural Overcast", "Golden Hour (Realistic)", "High-Contrast Studio", "Single Source Key Light", "Office Fluorescent (Relatable)", "Street Flash"
];

const LIGHTING_MOOD_OPTIONS = [
  "None / Standard", "Cinematic Chiaroscuro", "High-Dynamic Range (HDR)", "Moody Side-lighting", "Soft Global Illumination", "Subtle Specular Bloom", "Bounced Flash Shadows"
];

const ATMOSPHERE_OPTIONS = [
  "Clear / Sharp", "Subtle Lens Flare", "Natural Motion Blur", "Chromatic Aberration", "Light Sensor Grain", "Micro-Dust Motes"
];

const SKIN_TEXTURE_OPTIONS = [
  "Pore-Detail Hyper-Real", "Micro-Creases & Sweat", "Natural Oily Sheen", "Unedited Dermis", "Hyper-Realistic Pores", "Natural Subsurface Scattering"
];

const LENS_OPTIONS = [
  "50mm f/1.2 (Standard)", "35mm (Environment)", "85mm (Tight Portrait)", "24mm (Wide Documentary)", "Anamorphic (Cinematic Flare)"
];

const BODY_TYPE_OPTIONS = ["As in photo", "Slim / Lean", "Athletic / Toned", "Curvy / Hourglass", "Muscular / Built", "Statuesque / Tall", "Petite", "Natural / Average"];

const VIBE_OPTIONS = [
  "Photo-Documentary", "Candid Editorial", "Authentic Life", "Clean & Professional", "High-Fashion Raw", 
  "Sunday Morning Raw", "Street Photography", "Corporate Professional", "Quiet Luxury", "Modern Minimalist",
  "Vintage Archive", "Cinematic Noir", "Warm & Relatable", "High Dynamic Range"
];

const SCENE_OPTIONS = [
  // --- Entrepreneur / WFH ---
  "Sun-lit Scandi Home Office", "Modern Co-working Hub", "Urban Rooftop Meeting", "Minimalist Kitchen Workspace",
  "Airport Business Lounge", "Industrial Loft Atelier", "High-rise Boardroom", "Modern Coffee Shop Corner",
  "Design Agency Studio", "Sustainable Eco-Office", "Quiet Home Library", "Sleek Executive Suite",
  
  // --- Relatable / Lifestyle ---
  "Sunday Farmers Market", "Urban Street Walk", "Backyard Garden Lunch", "Modern Living Room",
  "Local Public Library", "Evening Dinner Party", "Morning Gym Session", "Modern Suburban Street",
  "Local Park Bench", "Boutique Retail Store", "Modern Train Station", "Lakeside Pier",
  
  // --- High-End / Destination ---
  "Swiss Alps Terrace", "Amalfi Coast Balcony", "Parisian Cafe Sidewalk", "Tokyo Neon Street",
  "Modern Art Gallery", "Luxury Superyacht Deck", "Private Jet Interior", "Santorini Cliffside",
  "Kyoto Bamboo Grove", "London Street Corner", "New York Central Park", "Icelandic Landscape"
];

const OUTFIT_OPTIONS = [
  "As in photo", "Smart Casual (WFH)", "Business Professional", "High-End Athleisure", "Minimalist Basics",
  "Sunday Best", "Denim & Linen Classic", "Autumn Layers (Knits)", "Modern Techwear", "Luxury Evening Wear",
  "Outdoor Adventure Gear", "Summer Florals"
];

const StudioDropdown: React.FC<{ label: string; value: string; options: any[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-3 group">
    <label className="text-[9px] font-bold uppercase tracking-widest text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-4 px-5 text-[12px] text-white/80 appearance-none focus:outline-none focus:border-[#dffe00]/50 focus:bg-white/[0.06] transition-all cursor-pointer hover:border-white/20"
      >
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt : opt.label;
          return <option key={val} value={val} className="bg-[#050505]">{lbl}</option>;
        })}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20"><ChevronDown className="w-4 h-4" /></div>
    </div>
  </div>
);

const AnchorSlot: React.FC<{ label: string; value: string | null; onUpload: (data: string | null) => void }> = ({ label, value, onUpload }) => (
  <div className="flex flex-col gap-3 flex-1 min-w-[140px]">
    <label className="text-[9px] font-bold uppercase tracking-widest text-center text-[#dffe00]/50">{label}</label>
    <div className="relative aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group cursor-pointer hover:border-white/20 transition-all">
      {value ? (
        <>
          <img src={value} className="w-full h-full object-cover" alt={label} />
          <button onClick={(e) => { e.preventDefault(); onUpload(null); }} className="absolute top-2 right-2 p-2 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/10 group-hover:text-[#dffe00]/40 transition-colors cursor-pointer p-4 text-center">
          <Plus className="w-5 h-5 stroke-[1px]" />
          <span className="text-[8px] uppercase tracking-widest">Portrait</span>
          <input type="file" className="hidden" accept="image/*" onChange={async e => {
             const file = e.target.files?.[0];
             if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (ev) => onUpload(ev.target?.result as string);
             }
          }} />
        </label>
      )}
    </div>
  </div>
);

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('landing');
  const [hasKey, setHasKey] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);

  const [brief, setBrief] = useState<CreativeBrief>({
    subjects: Array.from({ length: 5 }, () => ({ anchorImages: { frontal: null } })),
    vibe: VIBE_OPTIONS[0],
    scene: SCENE_OPTIONS[0],
    outfit: OUTFIT_OPTIONS[0],
    hairStyle: "As in photo",
    makeupPalette: "As in photo",
    skinStyle: "Natural & Realistic",
    skinTexture: SKIN_TEXTURE_OPTIONS[0],
    realismTuning: REALISM_PROFILES[0],
    bodyType: "As in photo",
    bustLine: "As in photo",
    waistRatio: "As in photo",
    hipsSculpt: "As in photo",
    cameraAngle: "Standard Eye Level",
    lighting: LIGHTING_OPTIONS[0],
    lightingMood: "High-Dynamic Range (HDR)",
    atmosphere: ATMOSPHERE_OPTIONS[0],
    lens: LENS_OPTIONS[0],
    aspectRatio: "16:9",
    numImages: 4,
    customPrompt: "",
    imageSize: "1K"
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  useEffect(() => {
    // Check for API Key in local storage or environment variable
    const storedKey = localStorage.getItem('gemini_api_key');
    const envKey = process.env.API_KEY; // This is injected by Vite at build time

    if (storedKey && storedKey.length > 5) {
      setHasKey(true);
    } else if (envKey && envKey.length > 5) {
      setHasKey(true);
    }
  }, []);

  const handleManualKeySubmit = () => {
    if (keyInput.length > 10) {
      localStorage.setItem('gemini_api_key', keyInput);
      setHasKey(true);
    } else {
      alert("Please enter a valid API Key");
    }
  };

  const clearKey = () => {
    localStorage.removeItem('gemini_api_key');
    setHasKey(false);
    setStep('landing');
  };

  const updateAnchor = (subjectIndex: number, value: string | null) => {
    const newSubjects = [...brief.subjects];
    newSubjects[subjectIndex] = { ...newSubjects[subjectIndex], anchorImages: { frontal: value } };
    setBrief(p => ({ ...p, subjects: newSubjects }));
  };

  const handleRefreshImage = async (imgId: string) => {
    const targetImage = images.find(img => img.id === imgId);
    if (!targetImage || !targetImage.prompt) return;

    setImages(prev => prev.map(p => p.id === imgId ? { ...p, status: 'generating', error: undefined } : p));
    
    try {
      const url = await generateTwinImage(brief, targetImage.prompt);
      setImages(prev => prev.map(p => p.id === imgId ? { ...p, status: 'completed', imageUrl: url } : p));
    } catch (e: any) {
      console.error("Refresh Error:", e);
      if (e.message === "KEY_RESET_REQUIRED") {
        clearKey();
        alert("API Key invalid or billing required.");
      } else {
        const errorMessage = e.message?.includes("No candidates") 
          ? "Safety Filter Blocked" 
          : e.message;
        setImages(prev => prev.map(p => p.id === imgId ? { ...p, status: 'failed', error: errorMessage } : p));
      }
    }
  };

  const initiateProduction = async () => {
    const uploadedCount = brief.subjects.filter(s => s.anchorImages.frontal).length;
    if (uploadedCount === 0) {
      alert(`Please upload at least one identity portrait to initiate production.`);
      return;
    }
    
    setIsGenerating(true);
    setImages([]); 

    try {
      const generated = await generateCampaignConcepts(brief);
      if (!generated || generated.length === 0) throw new Error("Could not produce group concept.");
      
      const initialImages: CampaignImage[] = Array.from({ length: brief.numImages }, (_, i) => ({
        id: `img-${i}`, 
        themeIndex: generated[0].themeIndex, 
        status: 'idle',
        prompt: generated[0].prompt // Store the prompt for refreshing later
      }));
      setImages(initialImages);

      // Scroll to output
      setTimeout(() => {
        document.getElementById('master-output')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

      for (const img of initialImages) {
        setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'generating' } : p));
        try {
          const url = await generateTwinImage(brief, img.prompt!);
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'completed', imageUrl: url } : p));
        } catch (e: any) {
          console.error("Twin Gen Error:", e);
          if (e.message === "KEY_RESET_REQUIRED") {
            clearKey();
            alert("API Key invalid or billing required.");
            break;
          }
          const errorMessage = e.message?.includes("No candidates") 
            ? "Safety Filter Blocked" 
            : e.message;
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'failed', error: errorMessage } : p));
        }
      }
    } catch (e: any) {
      console.error("Concept Gen Error:", e);
      if (e.message === "KEY_RESET_REQUIRED") {
        clearKey();
        alert("API Key invalid or billing required.");
      } else {
        alert(`Production Error: ${e.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const tabs = [
    { label: "Identity", icon: Users, id: 0 },
    { label: "Creative Direction", icon: Zap, id: 1 },
    { label: "Twin Likeness", icon: Crosshair, id: 2 },
    { label: "Production Optics", icon: Settings2, id: 3 }
  ];

  const goToNextTab = () => {
    if (activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!hasKey) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-6 text-white font-sans selection:bg-[#dffe00] selection:text-black relative">
        <div className="w-full max-w-md flex flex-col items-center z-10">
          
          {/* Lock Icon */}
          <div className="mb-10 p-5 rounded-[20px] border border-white/10 text-white/20">
             <Lock className="w-6 h-6 stroke-[1.5px]" />
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-tight font-semibold text-white text-center mb-3 tracking-tight">
             AI Twin Studio: <span className="text-[#dffe00]">Family</span>
          </h1>

          {/* Subtitle */}
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/30 mb-12 text-center">
             Connect studio access key to begin
          </p>

          {/* Real Input Field */}
          <div className="w-full mb-4 relative group">
             <div className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg py-1 px-1 focus-within:border-[#dffe00]/50 transition-all">
                <input 
                  type="password"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="Paste Gemini API Key"
                  className="w-full bg-transparent text-center text-[12px] text-white py-3 outline-none placeholder:text-white/20 tracking-wide"
                />
             </div>
          </div>

          {/* Button */}
          <button 
             onClick={handleManualKeySubmit} 
             disabled={keyInput.length < 5}
             className="w-full py-4 bg-white text-black text-[11px] font-bold uppercase tracking-[0.2em] rounded-lg hover:bg-[#dffe00] disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-12 shadow-[0_0_40px_-10px_rgba(255,255,255,0.1)]"
          >
             Activate Studio
          </button>

          {/* Divider & Footer */}
          <div className="w-full h-[1px] bg-white/5 mb-8" />
          
          <a href="https://aistudio.google.com/app/apikey" target="_blank" className="flex items-center gap-2 text-[9px] uppercase tracking-widest text-white/30 hover:text-white transition-colors">
             Get your free API key from Google <ExternalLink className="w-3 h-3" />
          </a>

        </div>
      </div>
    );
  }

  if (step === 'landing') {
    return (
      <div className="relative min-h-screen bg-[#050505] flex flex-col items-center justify-center overflow-hidden">
        <div className="relative z-10 text-center animate-fade-in">
          <h1 className="text-5xl md:text-6xl font-tight font-semibold text-white mb-4 headline-spacing">
            AI Twin Studio: <span className="text-[#dffe00]">Family</span>
          </h1>
          <p className="text-white/40 uppercase tracking-[0.5em] text-[10px] mb-12">Digital Identity Fabrication for Self & Friends</p>
          <button onClick={() => setStep('studio')} className="px-12 py-6 border border-white/10 rounded-full text-[11px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-black transition-all">Enter Studio</button>
        </div>
        
        <div className="absolute bottom-10 left-0 w-full flex justify-center z-20">
           <button onClick={clearKey} className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/20 hover:text-white transition-colors">
             <Key className="w-3 h-3" /> Update API Key
           </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#dffe00] selection:text-black pb-32">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-6 flex justify-between items-center bg-black/50 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setStep('landing')}>
          <span className="font-tight font-semibold text-[13px] tracking-[1px] text-white">
            AI Twin Studio: <span className="text-[#dffe00]">Family</span>
          </span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          {tabs.map((tab) => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest transition-all ${activeTab === tab.id ? 'text-[#dffe00]' : 'text-white/30 hover:text-white/60'}`}
            >
              <tab.icon className="w-3 h-3" />
              {tab.label}
            </button>
          ))}
        </div>
        <button className="text-[9px] font-bold uppercase tracking-widest text-white/40" onClick={() => setStep('landing')}>Exit</button>
      </nav>

      <main className="max-w-6xl mx-auto px-6 pt-32">
        {/* Tab Navigation (Mobile Only) */}
        <div className="flex md:hidden items-center justify-between mb-8 overflow-x-auto scrollbar-hide gap-6 pb-2">
           {tabs.map((tab) => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 text-[8px] font-bold uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === tab.id ? 'text-[#dffe00]' : 'text-white/30'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="animate-fade-in" key={activeTab}>
          {activeTab === 0 && (
            <section className="flex flex-col items-center">
              <div className="flex flex-col items-center gap-2 mb-12 text-center">
                <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase 01</span>
                <h2 className="text-3xl md:text-4xl font-tight font-semibold text-white headline-spacing">Identity Portraits</h2>
              </div>
              
              <div className="max-w-2xl bg-white/[0.03] border border-[#dffe00]/10 rounded-2xl p-6 mb-12 flex gap-4 items-start">
                <Info className="w-5 h-5 text-[#dffe00] shrink-0 mt-1" />
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#dffe00]">Likeness Advisory</span>
                  <p className="text-[11px] text-white/40 leading-relaxed">
                    Upload portraits for up to 5 subjects. The system uses these as literal identity templates. Clear frontal shots with neutral expressions provide the best results.
                  </p>
                </div>
              </div>

              <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 mb-24">
                {brief.subjects.map((s, i) => (
                  <AnchorSlot key={i} label={`Subject ${i + 1}`} value={s.anchorImages.frontal} onUpload={val => updateAnchor(i, val)} />
                ))}
              </div>

              <div className="w-full flex justify-center pt-16 border-t border-white/5">
                <button 
                  onClick={goToNextTab}
                  className="group flex items-center gap-4 px-12 py-6 bg-white/[0.03] border border-white/10 text-white text-[11px] font-bold uppercase tracking-[0.3em] rounded-full hover:bg-[#dffe00] hover:text-black transition-all duration-300"
                >
                  Next: Creative Direction
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 1 && (
            <section className="flex flex-col items-center">
               <div className="flex flex-col items-center gap-2 mb-12 text-center">
                <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase 02</span>
                <h2 className="text-3xl md:text-4xl font-tight font-semibold text-white headline-spacing">Creative Direction</h2>
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-8">
                <StudioDropdown label="Vibe" value={brief.vibe} options={VIBE_OPTIONS} onChange={v => setBrief(p => ({ ...p, vibe: v }))} />
                <StudioDropdown label="Location" value={brief.scene} options={SCENE_OPTIONS} onChange={v => setBrief(p => ({ ...p, scene: v }))} />
                <StudioDropdown label="Attire" value={brief.outfit} options={OUTFIT_OPTIONS} onChange={v => setBrief(p => ({ ...p, outfit: v }))} />
              </div>
              <div className="mt-12 w-full max-w-2xl mb-24">
                 <label className="text-[9px] font-bold uppercase tracking-widest text-[#dffe00]/50 block mb-3">Custom Creative Brief</label>
                 <textarea 
                    value={brief.customPrompt}
                    onChange={e => setBrief(p => ({ ...p, customPrompt: e.target.value }))}
                    placeholder="Describe specific details about the scene or subject interactions..."
                    className="w-full h-32 bg-white/[0.03] border border-white/10 rounded-xl p-5 text-[12px] text-white/80 focus:outline-none focus:border-[#dffe00]/50 transition-all resize-none"
                 />
              </div>

              <div className="w-full flex justify-center pt-16 border-t border-white/5">
                <button 
                  onClick={goToNextTab}
                  className="group flex items-center gap-4 px-12 py-6 bg-white/[0.03] border border-white/10 text-white text-[11px] font-bold uppercase tracking-[0.3em] rounded-full hover:bg-[#dffe00] hover:text-black transition-all duration-300"
                >
                  Next: Twin Likeness
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 2 && (
            <section className="flex flex-col items-center">
              <div className="flex flex-col items-center gap-2 mb-12 text-center">
                <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase 03</span>
                <h2 className="text-3xl md:text-4xl font-tight font-semibold text-white headline-spacing">Twin Likeness</h2>
              </div>
              <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 mb-24">
                <StudioDropdown label="Hair Style" value={brief.hairStyle} options={["As in photo", "Sleek Fade", "Soft Waves", "Slick Back", "Classic Blowout", "Long Textured"]} onChange={v => setBrief(p => ({ ...p, hairStyle: v }))} />
                <StudioDropdown label="Makeup / Grooming" value={brief.makeupPalette} options={["As in photo", "Soft Glam", "Natural", "Clean Shaven", "Heavy Stubble"]} onChange={v => setBrief(p => ({ ...p, makeupPalette: v }))} />
                <StudioDropdown label="Skin Dermis" value={brief.skinStyle} options={["Natural & Realistic", "Flawless Airbrushed", "Dewy / Radiant"]} onChange={v => setBrief(p => ({ ...p, skinStyle: v }))} />
                <StudioDropdown label="Skin Texture" value={brief.skinTexture} options={SKIN_TEXTURE_OPTIONS} onChange={v => setBrief(p => ({ ...p, skinTexture: v }))} />
                <StudioDropdown label="Realism Profile" value={brief.realismTuning} options={REALISM_PROFILES} onChange={v => setBrief(p => ({ ...p, realismTuning: v }))} />
                <StudioDropdown label="Body Type" value={brief.bodyType} options={BODY_TYPE_OPTIONS} onChange={v => setBrief(p => ({ ...p, bodyType: v }))} />
                <StudioDropdown label="Shoulder / Bust" value={brief.bustLine} options={["As in photo", "Natural", "Broad", "Slim"]} onChange={v => setBrief(p => ({ ...p, bustLine: v }))} />
                <StudioDropdown label="Waist Ratio" value={brief.waistRatio} options={["As in photo", "Snatched", "Natural", "Athletic"]} onChange={v => setBrief(p => ({ ...p, waistRatio: v }))} />
              </div>

              <div className="w-full flex justify-center pt-16 border-t border-white/5">
                <button 
                  onClick={goToNextTab}
                  className="group flex items-center gap-4 px-12 py-6 bg-white/[0.03] border border-white/10 text-white text-[11px] font-bold uppercase tracking-[0.3em] rounded-full hover:bg-[#dffe00] hover:text-black transition-all duration-300"
                >
                  Next: Production Optics
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 3 && (
            <section className="flex flex-col items-center">
              <div className="flex flex-col items-center gap-2 mb-12 text-center">
                <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase 04</span>
                <h2 className="text-3xl md:text-4xl font-tight font-semibold text-white headline-spacing">Production Optics</h2>
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <StudioDropdown label="Lighting Rig" value={brief.lighting} options={LIGHTING_OPTIONS} onChange={v => setBrief(p => ({ ...p, lighting: v }))} />
                <StudioDropdown label="Advanced Mood" value={brief.lightingMood} options={LIGHTING_MOOD_OPTIONS} onChange={v => setBrief(p => ({ ...p, lightingMood: v }))} />
                <StudioDropdown label="Atmosphere" value={brief.atmosphere} options={ATMOSPHERE_OPTIONS} onChange={v => setBrief(p => ({ ...p, atmosphere: v }))} />
                <StudioDropdown label="Technical Lens" value={brief.lens} options={LENS_OPTIONS} onChange={v => setBrief(p => ({ ...p, lens: v }))} />
                <StudioDropdown label="Camera Angle" value={brief.cameraAngle} options={["Standard Eye Level", "Low Angle Hero", "High Angle Soft", "Side Profile"]} onChange={v => setBrief(p => ({ ...p, cameraAngle: v }))} />
                <StudioDropdown 
                  label="Frame Aspect" 
                  value={brief.aspectRatio} 
                  options={[
                    {value:"16:9", label:"16:9 Cinematic"},
                    {value:"9:16", label:"9:16 Portrait (Mobile)"}, 
                    {value:"3:4", label:"3:4 Vertical"},
                    {value:"1:1", label:"1:1 Square (Social)"}, 
                    {value:"4:3", label:"4:3 Landscape"}
                  ]} 
                  onChange={v => setBrief(p => ({ ...p, aspectRatio: v as any }))} 
                />
                <StudioDropdown 
                  label="Output Resolution" 
                  value={brief.imageSize} 
                  options={[
                    {value:"1K", label:"Standard (1K)"},
                    {value:"2K", label:"High-Res (2K) - Requires Pro Key"}, 
                    {value:"4K", label:"Master-Res (4K) - Requires Pro Key"}
                  ]} 
                  onChange={v => setBrief(p => ({ ...p, imageSize: v as any }))} 
                />
              </div>

              <div className="mt-12 w-full flex flex-col items-center border-t border-white/5 pt-16">
                 <button onClick={initiateProduction} disabled={isGenerating} className="px-20 py-8 bg-[#dffe00] text-black text-[12px] font-bold uppercase tracking-[0.4em] rounded-full hover:bg-white hover:scale-105 transition-all duration-500 disabled:opacity-30 shadow-[0_20px_50px_rgba(223,254,0,0.3)]">
                   {isGenerating ? "Synthesizing Assets..." : "Initiate Studio Production"}
                 </button>
                 {isGenerating && <p className="mt-8 text-[9px] uppercase tracking-widest text-white/30 animate-pulse">Running identity synthesis...</p>}
              </div>
            </section>
          )}
        </div>

        {images.length > 0 && (
          <section id="master-output" className="flex flex-col items-center pt-24 animate-fade-in">
             <div className="w-full h-[1px] bg-white/5 mb-16" />
             <h2 className="text-3xl font-tight font-semibold text-white mb-16 headline-spacing">Master Output</h2>
             <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {images.map(img => (
                  <div key={img.id} className="aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-[32px] overflow-hidden group relative flex items-center justify-center">
                    {img.status === 'completed' ? (
                      <>
                        <img src={img.imageUrl} className="w-full h-full object-cover" alt="Master" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3 backdrop-blur-md">
                          <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-4 bg-white text-black rounded-full hover:scale-110 transition-transform"><Maximize2 className="w-5 h-5" /></button>
                          <button onClick={() => handleRefreshImage(img.id)} className="p-4 bg-white text-black rounded-full hover:scale-110 transition-transform"><RotateCcw className="w-5 h-5" /></button>
                          <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = 'twin-output.png'; link.click(); }} className="p-4 bg-[#dffe00] text-black rounded-full hover:scale-110 transition-transform"><Download className="w-5 h-5" /></button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-4 text-center px-6">
                         {img.status === 'failed' ? (
                           <>
                             <AlertCircle className="w-6 h-6 text-red-500/50" />
                             <span className="text-[10px] uppercase text-red-500/50 leading-tight">{img.error || "Synthesis Failed"}</span>
                             <button onClick={() => handleRefreshImage(img.id)} className="mt-2 text-[9px] font-bold uppercase tracking-widest text-[#dffe00] hover:underline flex items-center gap-2">
                                <RotateCcw className="w-3 h-3" /> Retry Production
                             </button>
                           </>
                         ) : (
                           <>
                             <Loader2 className="w-8 h-8 text-[#dffe00]/20 animate-spin" />
                             <span className="text-[8px] uppercase tracking-widest text-white/20">Synthesizing...</span>
                           </>
                         )}
                      </div>
                    )}
                  </div>
                ))}
             </div>
          </section>
        )}
      </main>

      <footer className="max-w-6xl mx-auto px-6 py-12 mt-20 border-t border-white/5 flex flex-col items-center justify-center gap-6">
        <button 
           onClick={clearKey} 
           className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.03] hover:bg-white/[0.08] transition-all group"
        >
           <Key className="w-3 h-3 text-white/40 group-hover:text-[#dffe00] transition-colors" />
           <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 group-hover:text-white transition-colors">Update API Key</span>
        </button>
        <span className="text-[9px] uppercase tracking-widest text-white/10">AI Twin Studio: Family</span>
      </footer>

      {previewImage && <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8" onClick={() => setPreviewImage(null)}><img src={previewImage} className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl" alt="Preview" /></div>}
    </div>
  );
};

export default App;
